import { Box, Link, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging, FormatMeasurementUnit, FormatPackagingUnitPrice, FormatPackagingVolumePrice, formatPriceByBillingType, round } from '@kheops/utils';
import { Duration_Type_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import i18n from '../../i18n';
import { PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { FormatPackagingLabels } from '../../packaging/packaging-formatter';
import ShelfLifeDurationDisplay from '../productPage/Product/ShelfLifeDurationDisplay';
import ProductUtils, { isNonFoodProduct } from '../common/products.utils';
import { BaseUnit, Packaging, Product } from './PackagingPage';

const COLLAPSED_MAX_HEIGHT = 240;

export interface PackagingPageSpecificationsProps {
  product: Product;
  baseUnit: BaseUnit;
  packaging: Packaging;
}

export default function PackagingPageSpecifications({ product, baseUnit, packaging }: PackagingPageSpecificationsProps): React.JSX.Element {
  const { t } = useTranslation(['discovery', 'products', 'common', 'product-classification']);
  const currencyFormat = useCurrencyFormat();
  const [isExpanded, setIsExpanded] = useState(false);
  const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const specificationsMap = useMemo(() => {
    const map = new Map<string, { label?: string; value?: string, component?: React.JSX.Element }>();

    const volumePrice = FormatPackagingVolumePrice(packaging, baseUnit);

    map.set('subFamily', {
      label: t('discovery:departments'),
      value: t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`),
    });

    if (product.generic_product_code) {
      map.set('product_nature', {
        label: t('products:product_nature'),
        value: t(`product-classification:${product.generic_product_code}`),
      });
    }

    if (product.labels.length >= 1) {
      map.set('labels', {
        label: t('contracts:quality'),
        value: FormatPackagingLabels(product.labels).map(({ alt }) => alt).join(', '),
      });
    }

    map.set('basePackaging', {
      label: t('products:base_unit_one'),
      value: FormatBaseUnitQuantity(baseUnit, 'fr', numberFormat) || '',
    });

    map.set('packaging', {
      label: t('contracts:packaging_type'),
      value: FormatLogisticalPackaging(packaging, 'fr', baseUnit) || '',
    });

    map.set('price', {
      label: t('price:price_without_tax'),
      value: `${formatPriceByBillingType(currencyFormat.format(round(packaging.price)), baseUnit.billing_type)}`,
    });

    map.set('unitPrice', {
      label: t('price:price_without_tax_per_base_unit'),
      value: `${formatPriceByBillingType(currencyFormat.format(round(FormatPackagingUnitPrice(packaging))), baseUnit.billing_type)}`,
    });

    map.set('volumePrice', {
      label: t('price:price_without_tax_per_volume'),
      value: `${currencyFormat.format(round(volumePrice!.value))}/${FormatMeasurementUnit(volumePrice!.unit, 'fr', 1)}` || '',
    });

    map.set('tvaRate', {
      label: t('contracts:vat_rate'),
      value: new Intl.NumberFormat(i18n.resolvedLanguage, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        style: 'percent',
      }).format(product.vat_rate!),
    });

    map.set('shelf_life_duration', {
      component: <ShelfLifeDurationDisplay
        duration={product.shelf_life_duration}
        durationType={product.shelf_life_duration_type as Duration_Type_Enum | undefined}
        titleVariant="bodySmall"
        titleAndValueGap={0.5}
        valueTypographyVariant="bodyLarge"
      />,
    });

    if (baseUnit.gtin) {
      map.set('gtin', {
        label: t('common:barcode'),
        value: baseUnit.gtin,
      });
    }

    if (product.brand) {
      map.set('brand', {
        label: t('products:brand'),
        value: product.brand,
      });
    }

    if (product.alcohol_by_volume && ProductUtils.isAlcoholProduct(product.sub_family as Product_Sub_Family_Name_Enum)) {
      map.set('alcohol_by_volume', {
        label: t('products:alcohol_by_volume'),
        value: `${product.alcohol_by_volume} ${t('products:alcohol_by_volume_unit')}`,
      });
    }

    if (product.ingredients || product.is_exempt_from_ingredient_list) {
      map.set('ingredients', {
        label: t('products:ingredient_list'),
        value: product.is_exempt_from_ingredient_list ? t('products:no_ingredient') : product.ingredients!,
      });
    }

    if (product.allergens || product.has_no_allergen_declaration) {
      map.set('allergens', {
        label: t('products:allergen_list'),
        value: product.has_no_allergen_declaration ? t('products:no_allergen') : product.allergens!,
      });
    }

    if (!isNonFoodProduct(product.sub_family as Product_Sub_Family_Name_Enum)) {
      map.set('preservation_method', {
        label: t('products:buyer_preservation_method'),
        value: product.preservation_method ? t(`products:preservation_method_${product.preservation_method.toLowerCase()}`) : t('common:not_filled'),
      });
    }

    return map;
  }, [product, baseUnit, packaging]);

  const hasOverflow = useMemo(() => specificationsMap.size > 4, [specificationsMap]);

  return (
    <Box>
      <Typography variant="titleMedium" sx={{ mb: 1 }}>
        {t('products:specifications')}
      </Typography>
      <Box
        sx={{
          maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT,
          overflow: isExpanded ? 'visible' : 'hidden',
        }}
      >
        {Array.from(specificationsMap.values()).map((specification) => (
          <Box
            key={specification.label}
            sx={{
              mb: 1,
              pb: 2,
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: 'divider',
            }}
          >
            {specification.component
              ? (specification.component)
              : (
                <>
                  <Typography
                    variant="bodyMedium"
                    color="onSurfaceVariant"
                    sx={{ mb: 0.5 }}
                  >
                    {specification.label}
                  </Typography>
                  <Typography variant="bodyLarge">{specification.value}</Typography>
                </>
              )}
          </Box>
        ))}
      </Box>
      {hasOverflow && (
        <Link
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            color: 'onSurfaceVariant.main',
          }}
        >
          <Typography variant="labelSmall">
            {t(isExpanded ? 'common:see_less' : 'common:see_more')}
          </Typography>
        </Link>
      )}
    </Box>
  );
}
